// src/com/menu_info/MenuInfo.js
import React, {useEffect, useState, useRef} from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import { IconButton } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft'; // add to left
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight'; // remove from left
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import {ContentConainer, ContentHeader, ContentBody, ToolBox} from '../../common/TopViewControl';
import RadioSelector from '../../common/RadioSelector';
import VzmSelector from '../../common/VzmSelector';
import { AppObject, AppStyle, AppWord, UserClass } from '../../value/AppConst';
import { adminMenuTreeRepo, allMenuTreeRepo, authTableTreeRepo, menuListNotForRepo } from '../../value/AppModel';
import {
    LIST_MENU_INFO_FOR, LIST_MENU_INFO_NOT_FOR, LIST_MENU_INFO,
    ADD_MENU_AUTH_ALLOC, REMOVE_MENU_AUTH_ALLOC,
    makeMenuTree
} from './MenuInfoGql';
import MenuInfoTree from './MenuInfoTree';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';


const ControlBox = styled(Box)({flexGrow:1, display:'flex', alignItems:'center', height:60});
const RadioBox = styled(Box)({marginRight:10});

const AllTreeBox = styled(Box)({overflow: 'auto', height: '100%',});

const AdminBox = styled(Box)({
    display: 'flex', height: '100%', width: '100%'
});
const HeaderAndTreeBox = styled(Box)({
    padding: 10, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box',
});
const BoxHeader = styled(Box)({fontWeight: 'bold', padding: 10});
const FillingBodyBox = styled(Box)({
    flexGrow: 1, border: AppStyle.BorderCCC, borderRadius: 5,
    position:'relative', minWidth: 300,
});
const InnerWrapperForScroll = styled(Box)({
    position: 'absolute',
    left:0, top:0, right:0, bottom:0,
    padding: '3px 0px 3px 5px', // backgroundColor:'yellow',
    overflow:'auto'
});

const AdminWorkBox = styled(Box)({
    flexGrow: 1, padding: 10, display: 'flex', flexDirection: 'column'
});

const MenuRecordBox = styled(Box)({
    display: 'flex', alignItems: 'center', fontSize: '0.9rem', padding: '5px 10px 5px 10px',
    borderBottom: AppStyle.BorderCCC, height:32,
    '&:hover': {backgroundColor: '#f8f8f8'},
    '&:last-of-type': {borderBottom: 'none'},
});

const MenuTypeDict = {
    id: 'manuType', valueKey: 'value',
    selectList: [
        {value: AppWord.ALL, label: '모든 메뉴'},
        {value: 'admin', label: '사용자 그룹별 메뉴 관리'},
    ]
};

const ClassSelectDict = {
    id: 'classSelect', //label: '사용자 그룹',
    selectList: Object.values(UserClass).filter(c=>c.id>0).map((obj)=>{
        return {classSelect: obj.id, label: obj.name};
    })
};

export default function MenuInfo() {
    const allmenuTree = useReactiveVar(allMenuTreeRepo);
    const adminMenuTree = useReactiveVar(adminMenuTreeRepo);
    const menuListNotFor = useReactiveVar(menuListNotForRepo);
    const [menuType, setMenuType] = useState(AppWord.ALL);
    const [classSelect, setClassSelect] = useState(UserClass.homePlusAdmin.id);
    const [menuHover, setMenuHover] = useState(null);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const renderRef = useRef(0);
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getMenuInfoList, responseList] = useLazyQuery(LIST_MENU_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data) => {onCompleteGetAllList(data)},
		onError: (error) => {addAlert({title: '전체 메뉴 조회 오류', message: [error.message]})},
    });

    const [getMenuInfoListFor, responseListFor] = useLazyQuery(LIST_MENU_INFO_FOR, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data) => {onCompleteGetListFor(data)},
		onError: (error) => {addAlert({title: '그룹별 메뉴 조회 오류', message: [error.message]})},
    });

    const [getMenuInfoListNotFor, responseListNotFor] = useLazyQuery(LIST_MENU_INFO_NOT_FOR, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data) => {onCompleteGetListNotFor(data)},
		onError: (error) => {addAlert({title: '그룹별 제외 메뉴목록 조회 오류', message: [error.message]})},
    });
    const [addMenuAuthAlloc, responseAdd] = useMutation( ADD_MENU_AUTH_ALLOC, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => {addAlert({title: '메뉴 추가 오류', message: [error.message]})}
	} );
    const [removeMenuAuthAlloc, responseRemove] = useMutation( REMOVE_MENU_AUTH_ALLOC, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => {addAlert({title: '메뉴 삭제 오류', message: [error.message]})}
	});

    useEffect(()=>{
        if(renderRef.current === 0) {
            getMenuInfoList();
            renderRef.current = 1;
        }
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetAllList = (data) => {
        // find current itemSelected and reset.
        if(data.menuInfoList) {
            allMenuTreeRepo(makeMenuTree(data.menuInfoList));
            lastReloadRef.current = new Date().getTime()/1000;
        }
    };

    const onCompleteGetListFor = (data) => {
        // find current itemSelected and reset.
        if(data.menuInfoListFor) {
            adminMenuTreeRepo(makeMenuTree(data.menuInfoListFor));
            getMenuInfoListNotFor({variables: {authClassId:'' + classSelect}});
        }
    };

    const onCompleteGetListNotFor = (data) => {
        // find current itemSelected and reset.
        if(data.menuInfoListNotFor) menuListNotForRepo(data.menuInfoListNotFor);
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.menuAuthAllocAdd.ok) {
            getMenuInfoListFor({variables: {authClassId:'' + classSelect}});
        }
        else
            addAlert({title: '메뉴 추가 오류', message: [data.menuAuthAllocAdd.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.menuAuthAllocRemove.ok) {
            getMenuInfoListFor({variables: {authClassId:'' + classSelect}});
        }
        else
            addAlert({title: '메뉴 삭제 오류', message: [data.menuAuthAllocRemove.message]});
    };

    const onChangeMenuType = (id, val, err) => {
        setMenuType(val);
        if(val === 'admin' && classSelect > 0 && adminMenuTree.length === 0) {
            getMenuInfoListFor({variables: {authClassId:'' + classSelect}});
        }
    };

    const onChangeClassSelect = (id, val, err) => {
        setClassSelect(val);
        getMenuInfoListFor({variables: {authClassId:'' + val}});
    };

    const onClickAddMenu = (menu) => {
        setMenuHover(null);
        addMenuAuthAlloc({variables: {menuAuthAlloc: {authClassId: ''+classSelect, menuId: menu.menuId}}});
    };

    const onClickRemoveMenu = (menu) => {
        removeMenuAuthAlloc({variables: {menuAuthAlloc: {authClassId: ''+classSelect, menuId: menu.menuId}}});
    };

    const onClickToolIcon = (id, menu) => {
        if(id==='remove') onClickRemoveMenu(menu);
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            enqueueSnackbar('1분 이내에는 재조회 할 수 없습니다.', {variant:'info', autoHideDuration:2000});
        }
        else getMenuInfoList();
    };

    /*
	CALL wps_set_auth_table('pr_menu_auth_alloc_add', '1', v_msg);
	CALL wps_set_auth_table('pr_menu_auth_alloc_remove', '1', v_msg);
    */

    const canAdd  = checkAuth(authTableTree.tree.pr_menu_auth_alloc_add);
    const canRemove = checkAuth(authTableTree.tree.pr_menu_auth_alloc_remove);

    const loadingAny
    = responseList.loading
    || responseListFor.loading
    || responseListNotFor.loading
    || responseAdd.loading
    || responseRemove.loading;

    const renderAllMenuTree = () => {
        return(
            <AllTreeBox>
                <MenuInfoTree
                    treeData={allmenuTree}
                    tools={[]}
                    onClickTool={(id, menu)=>{}}
                    horizontal={true}
                />
            </AllTreeBox>
        );
    };

    const renderAdminMenuTree = () => {
        return(
            <AdminBox>
                <HeaderAndTreeBox>
                    <BoxHeader>이용중인 메뉴</BoxHeader>
                    <FillingBodyBox>
                        <InnerWrapperForScroll>
                            <MenuInfoTree
                                treeData={adminMenuTree}
                                tools={canRemove ? [
                                    {
                                        icon: <ArrowCircleRightIcon />,
                                        id: 'remove',
                                        color: AppStyle.mui.warning
                                    }
                                ] : []}
                                onClickTool={onClickToolIcon}
                                horizontal={false}
                            />
                        </InnerWrapperForScroll>
                    </FillingBodyBox>
                </HeaderAndTreeBox>
                <AdminWorkBox>
                    <BoxHeader>이용하지 않는 메뉴</BoxHeader>
                    <FillingBodyBox>
                        <InnerWrapperForScroll>
                            <Box>
                                {
                                    menuListNotFor.map((menu, index)=>(
                                        <MenuRecordBox key={index}
                                            onMouseEnter={()=>setMenuHover(menu.menuId)}
                                            onMouseLeave={()=>setMenuHover(null)}
                                        >
                                            <IconButton
                                                color={AppStyle.mui.primary}
                                                style={{marginRight:10}}
                                                onClick={()=>onClickAddMenu(menu)}
                                                size={AppWord.SMALL}
                                                disabled={menuHover !== menu.menuId || !canAdd || loadingAny}
                                            >
                                                <ArrowCircleLeftIcon />
                                            </IconButton>
                                            <span style={{color:'#777'}}>({index+1})</span> &nbsp;
                                            {menu.menuName}
                                        </MenuRecordBox>
                                    ))
                                }
                            </Box>
                        </InnerWrapperForScroll>
                    </FillingBodyBox>
                </AdminWorkBox>
            </AdminBox>
        );
    };

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    <RadioBox>
                        <RadioSelector
                            dict={MenuTypeDict}
                            value={menuType}
                            onChange={onChangeMenuType}
                            horizontal={true}
                        />
                    </RadioBox>
                    {
                        menuType === 'admin' ?
                        <VzmSelector
                            dict={ClassSelectDict}
                            value={classSelect}
                            onChange={onChangeClassSelect}
                        />
                        : <div style={{
                            fontSize:'0.8rem', backgroundColor: 'yellow',
                            padding: 5, borderRadius: 15, border: AppStyle.BorderCCC
                            }}>
                            흐린글씨-기존에도 메뉴만 있었음. /// 빨간글씨-추가작업 필요.
                        </div>
                    }
                </ControlBox>
                {
                    menuType === AppWord.ALL &&
                    <ToolBox>
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    </ToolBox>
                }
            </ContentHeader>
            <ContentBody>
                {menuType===AppWord.ALL ? renderAllMenuTree() : renderAdminMenuTree()}
            </ContentBody>
        </ContentConainer>
    );
}